import React from 'react';
import { Box, IconButton, Typography, Select, MenuItem, FormControl } from '@mui/material';
import { NavigateBefore, NavigateNext, FirstPage, LastPage } from '@mui/icons-material';

/**
 * Reusable Pagination Controls Component
 * Odoo-style pagination with item range display
 * 
 * @param {number} page - Current page (0-indexed)
 * @param {number} rowsPerPage - Number of rows per page
 * @param {number} total - Total number of items
 * @param {function} onPageChange - Callback when page changes
 * @param {function} onRowsPerPageChange - Callback when rows per page changes
 * @param {array} rowsPerPageOptions - Available rows per page options
 * @param {boolean} showRowsPerPage - Whether to show rows per page selector
 * @param {boolean} showFirstLast - Whether to show first/last page buttons
 * @param {object} sx - Additional styles
 */
const PaginationControls = ({
  page = 0,
  rowsPerPage = 10,
  total = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 80, 100],
  showRowsPerPage = false,
  showFirstLast = false,
  sx = {},
}) => {
  const startItem = page * rowsPerPage + 1;
  const endItem = Math.min((page + 1) * rowsPerPage, total);
  const totalPages = Math.ceil(total / rowsPerPage);

  const handlePrevious = () => {
    if (page > 0) {
      onPageChange(page - 1);
    }
  };

  const handleNext = () => {
    if (page < totalPages - 1) {
      onPageChange(page + 1);
    }
  };

  const handleFirst = () => {
    onPageChange(0);
  };

  const handleLast = () => {
    onPageChange(totalPages - 1);
  };

  if (total === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ...sx,
      }}
    >
      {showRowsPerPage && (
        <FormControl size="small" sx={{ minWidth: 70 }}>
          <Select
            value={rowsPerPage}
            onChange={(e) => onRowsPerPageChange(e.target.value)}
            sx={{
              fontSize: '13px',
              height: 28,
              '& .MuiSelect-select': { py: 0.5 },
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Item range display */}
      <Typography
        variant="body2"
        sx={{
          fontSize: '13px',
          color: '#666',
          display: { xs: 'none', sm: 'block' },
          minWidth: 80,
          textAlign: 'center',
        }}
      >
        {startItem}-{endItem} / {total}
      </Typography>

      {/* Navigation buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {showFirstLast && (
          <IconButton
            size="small"
            onClick={handleFirst}
            disabled={page === 0}
            sx={{
              borderRadius: 0,
              px: 0.5,
              py: 0.25,
              borderRight: '1px solid #dee2e6',
              '&.Mui-disabled': { color: '#ccc' },
            }}
          >
            <FirstPage sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        
        <IconButton
          size="small"
          onClick={handlePrevious}
          disabled={page === 0}
          sx={{
            borderRadius: 0,
            px: 0.5,
            py: 0.25,
            borderRight: '1px solid #dee2e6',
            '&.Mui-disabled': { color: '#ccc' },
          }}
        >
          <NavigateBefore sx={{ fontSize: 18 }} />
        </IconButton>
        
        <IconButton
          size="small"
          onClick={handleNext}
          disabled={page >= totalPages - 1}
          sx={{
            borderRadius: 0,
            px: 0.5,
            py: 0.25,
            '&.Mui-disabled': { color: '#ccc' },
          }}
        >
          <NavigateNext sx={{ fontSize: 18 }} />
        </IconButton>

        {showFirstLast && (
          <IconButton
            size="small"
            onClick={handleLast}
            disabled={page >= totalPages - 1}
            sx={{
              borderRadius: 0,
              px: 0.5,
              py: 0.25,
              borderLeft: '1px solid #dee2e6',
              '&.Mui-disabled': { color: '#ccc' },
            }}
          >
            <LastPage sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default PaginationControls;
